import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, ThemeProvider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { createTheme } from '@mui/material/styles';
import { Title } from 'react-head';
import LoginForm from '../../components/Common/LoginForm';
import useAuth from '../../hooks/useAuth';
import VaultLoginGraphic from '../../components/Common/VaultLogin/VaultLoginGraphic';
import utils from '../../utils/Utils';
import nimbusLightTheme from '../../Theme/NimbusTheme/nimbusLightTheme';
import SwalToast from '../../components/Common/SwalToast';
import useCustomTheme from '../../hooks/useCustomTheme';

const ProductName = 'Vault';

const VaultLoginPage = () => {
  const navigate = useNavigate();
  const {
    loginProductRequest,
    forgotPassword,
    getIDPList,
    getProductsAssociatedToTenant,
    loginIDP,
    getTokenFromAuthCode,
    getTokenFromAuthCodeForIDP,
  } = useAuth();
  const { fetchThemeConfiguration } = useCustomTheme();

  const [idpList, setIdpList] = useState([]);
  const [productDetails, setProductDetails] = useState({});
  const [orgDetails, setOrgDetails] = useState({});
  const [customTheme, setCustomTheme] = useState(null);
  const { state = {} } = useLocation();
  const tenant = useMemo(() => localStorage.getItem('TENANT') || '', []);

  const getAuthHeaders = useCallback(async () => {
    const deviceId = await utils.getOrCreateDeviceId();
    return {
      'X-Tenant-ID': tenant,
      'X-Device-ID': deviceId,
    };
  }, [tenant]);

  const handleIDPCallback = useCallback(
    async (rawState) => {
      const { redirectURL, isFromIDP, productId } = JSON.parse(
        decodeURIComponent(rawState)
      );
      if (!isFromIDP) return;

      const authCode = new URLSearchParams(window.location.search).get('code');
      if (!authCode) return;

      const headers = await getAuthHeaders();

      const res = await getTokenFromAuthCodeForIDP(
        { ...headers },
        { code: authCode, product_id: productId }
      );
      const { access_token, refresh_token, session_id } = res.data;

      const finalRedirect = utils.updateUrlParams(
        new URL(redirectURL),
        {
          access_token,
          refresh_token,
          realm_id: tenant,
          session_id,
          device_id: headers['X-Device-ID'],
        },
        ['code']
      );

      window.location.href = finalRedirect.toString();
    },
    [tenant, getAuthHeaders, getTokenFromAuthCodeForIDP]
  );

  useEffect(() => {
    if (!tenant) {
      navigate('/launcher');
      return;
    }

    const params = new URLSearchParams(window.location.search);

    const errorParam = params.get('error');
    if (errorParam) {
      const errorMessage = decodeURIComponent(errorParam);

      const cleanUrl = utils.updateUrlParams(
        new URL(window.location.href),
        {},
        ['error']
      );
      window.history.replaceState({}, document.title, cleanUrl);

      const showError = () => {
        SwalToast({
          icon: 'error',
          title: errorMessage,
        });
      };

      // Using requestAnimationFrame to ensure DOM is ready and snackbar is initialized
      requestAnimationFrame(() => {
        setTimeout(showError, 0);
      });
    }

    const stateParam = params.get('state') || params.get('RelayState');
    if (stateParam) {
      handleIDPCallback(stateParam);
    }
  }, [navigate, handleIDPCallback, tenant]);

  useEffect(() => {
    async function loadIdpsAndTheme() {
      if (!tenant) return;

      try {
        const headers = await getAuthHeaders();
        const resp = await getIDPList({
          'X-Tenant-ID': headers['X-Tenant-ID'],
        });
        const response = await getProductsAssociatedToTenant({
          'X-Tenant-ID': headers['X-Tenant-ID'],
        });
        setIdpList(resp.data.data || []);
        setProductDetails(
          response.data.data.find((item) => item?.name === ProductName) || {}
        );
        setOrgDetails(response.data);
      } catch {
        setIdpList([]);
      }

      try {
        const headers = await getAuthHeaders();
        const themeConfig = await fetchThemeConfiguration(
          headers['X-Tenant-ID'],
          ProductName,
          'light'
        );
        if (themeConfig?.data?.saved_theme) {
          setCustomTheme(themeConfig.data.saved_theme);
        }
      } catch {
        console.error('Failed to fetch theme configuration');
      }
    }
    loadIdpsAndTheme();
  }, []);

  const handleLogin = useCallback(
    async (values) => {
      const payload = {
        email: values.email,
        password: values.password,
        product_domain: productDetails?.root_url,
      };

      const headers = await getAuthHeaders();

      const response = await loginProductRequest(headers, payload);
      const { redirect_url: redirectUrl, session_id: sessionId } =
        response.data;
      if (redirectUrl) {
        const code = new URL(redirectUrl).searchParams.get('code');
        const tokenRes = await getTokenFromAuthCode(
          { 'X-Tenant-ID': headers['X-Tenant-ID'] },
          {
            code,
            product_id:
              state?.productId && state.productId !== 'null'
                ? state.productId
                : productDetails?.id || '',
          }
        );

        const { access_token, refresh_token } = tokenRes.data;
        const baseUrl = new URL(
          state?.redirect && state.redirect !== 'null'
            ? state.redirect
            : redirectUrl
        );
        const updated = utils.updateUrlParams(
          baseUrl,
          {
            access_token,
            refresh_token,
            realm_id: headers['X-Tenant-ID'],
            session_id: sessionId,
            device_id: headers['X-Device-ID'],
          },
          ['code']
        );

        window.location.href = updated.toString();
      }
    },
    [
      getAuthHeaders,
      loginProductRequest,
      getTokenFromAuthCode,
      state,
      productDetails,
    ]
  );

  const handleForgotPassword = useCallback(
    async (email) => {
      const headers = await getAuthHeaders();

      const response = await forgotPassword(
        {
          'X-Tenant-ID': headers['X-Tenant-ID'],
        },
        { email, product: ProductName }
      );
      if (response?.status === 200) {
        SwalToast({
          icon: 'success',
          title:
            response?.data?.message ||
            'Password reset link sent to your email.',
        });
      }
    },
    [forgotPassword]
  );

  const handleIDPLogin = useCallback(async () => {
    const headers = await getAuthHeaders();
    await loginIDP(productDetails?.root_url, state, headers);
  }, [getAuthHeaders, loginIDP, state, productDetails]);

  const theme = useMemo(() => {
    if (customTheme) {
      return createTheme(_.merge(_.cloneDeep(nimbusLightTheme), customTheme));
    }
    return nimbusLightTheme;
  }, [customTheme]);

  return (
    <>
      <Title>MRM Vault</Title>
      <ThemeProvider theme={theme}>
        <Grid
          container
          height="100vh"
          sx={{
            backgroundColor: theme.palette?.background?.default,
            zIndex: 1,
            overflowX: 'auto',
          }}
        >
          <VaultLoginGraphic />
          <Grid
            container
            item
            xs={12}
            sm={12}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <LoginForm
              onLoginClick={handleLogin}
              onForgotPassword={handleForgotPassword}
              idpList={idpList}
              onIDPLogin={handleIDPLogin}
              onlyADLoginEnabled={orgDetails?.ad_login_only === 'true'}
              showForgotPassword={orgDetails?.allow_forgetpassword === 'true'}
            />
          </Grid>
        </Grid>
      </ThemeProvider>
    </>
  );
};

export default VaultLoginPage;
